import { useEffect, useRef, useState } from "react";

type Stat = { value: number; prefix?: string; suffix?: string; label: string; display?: (n: number) => string };

const STATS: Stat[] = [
  { value: 100, prefix: "$", suffix: "K", label: "Cleared before 18" },
  { value: 1, suffix: "M+", label: "Personal sales", display: (n) => `${n}M+` },
  { value: 18, label: "Years old" },
  { value: 0, label: "By application", display: () => "—" },
];

function useCountUp(target: number, start: boolean, duration = 1400) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf = 0;
    const t0 = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target, duration]);
  return n;
}

function StatItem({ s, start }: { s: Stat; start: boolean }) {
  const n = useCountUp(s.value, start);
  const rounded = Math.round(n);
  const text = s.display ? s.display(rounded) : `${s.prefix ?? ""}${rounded}${s.suffix ?? ""}`;
  return (
    <div className="flex flex-col items-center md:items-start gap-2 text-center md:text-left">
      <span
        style={{
          fontFamily: "'Fraunces', serif",
          fontVariationSettings: "'opsz' 144, 'SOFT' 40",
          fontWeight: 400,
          fontSize: "clamp(2rem, 3.6vw, 2.75rem)",
          lineHeight: 1,
          letterSpacing: "-0.025em",
          color: "var(--gold-strong)",
        }}
      >
        {text}
      </span>
      <span className="mono-label">{s.label}</span>
    </div>
  );
}

export default function ProofStrip() {
  const ref = useRef<HTMLDivElement>(null);
  const [start, setStart] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) { setStart(true); return; }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { setStart(true); io.disconnect(); } }),
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className="access-container mt-16 md:mt-20">
      <div
        className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-6 py-8 md:py-10 px-6 md:px-10"
        style={{
          borderTop: "1px solid var(--border-soft)",
          borderBottom: "1px solid var(--border-soft)",
        }}
      >
        {STATS.map((s, i) => <StatItem key={i} s={s} start={start} />)}
      </div>
    </div>
  );
}
